import type { DateTime } from "luxon";
import { type FC, type ReactNode, useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAnalytics } from "../../../../contexts/analytics/hooks";
import { useDraftMode } from "../../../../contexts/DraftModeContext";
import { useHospitalData } from "../../../../contexts/HospitalDataProvider";
import { StaffingChanges, type TabType } from "../../../StaffingChanges/StaffingChanges";
import type { MonthlyDayData } from "./monthlyJobUtils";

/**
 * Which surface an action was invoked from. Only ever an analytics property — both surfaces open exactly
 * the same panel, and the day this becomes a behavioural branch is the day they start to diverge.
 */
export type MonthlyDayActionSource = "cell" | "sidebar";

/**
 * The `StaffingChanges` tabs the monthly view opens directly. The panel itself offers more (swap, move,
 * recurring) once open — these are only the three the day's own actions land the user on.
 */
export type MonthlyDayActionTab = Extract<TabType, "add-nurse" | "remove-nurse" | "create-shift">;

/**
 * Context an action carries when it was invoked from ONE shift-time window rather than from the whole
 * day — the sidebar's per-window row knows its own times and, when the window holds a single job, its job.
 */
export type MonthlyDayActionPrefill = {
	startTime: DateTime;
	endTime: DateTime;
	/** Only when the window implies exactly ONE job. A prefilled shift on a guessed job is worse than none. */
	jobId?: string;
};

/**
 * `StaffingChanges` addresses a day as `weeks[weekIndex].weeklyShifts[shiftNo].days[dayNo]`, and every
 * day-level caller (`DayViewGaps`, `Recommendations`, `DayViewItem`) reaches the day through bucket `0`:
 * the panel re-derives the real shift bucket from the times the user picks, so the bucket passed in only
 * has to name a day that exists.
 */
const DAY_LEVEL_SHIFT_NO = 0;

/**
 * Renders the panel and resolves the department's roles for it.
 *
 * Split out purely so `useHospitalData()` is called only once a panel is actually open: reading it in
 * the hook itself would subscribe all 42 grid cells to the hospital-data context for a value none of
 * them needs until an action is invoked (see `MonthlyJobCell`'s memo note).
 */
const MonthlyDayStaffingChanges: FC<{
	tab: MonthlyDayActionTab;
	prefill: MonthlyDayActionPrefill | undefined;
	weekIndex: number;
	dayNo: number;
	onClose: () => void;
}> = ({ tab, prefill, weekIndex, dayNo, onClose }) => {
	const { departmentNurseTypes } = useHospitalData();
	return (
		<StaffingChanges
			setIsAdd={onClose}
			onClose={onClose}
			defaultTab={tab}
			dayNo={dayNo}
			shiftNo={DAY_LEVEL_SHIFT_NO}
			weekIndex={weekIndex}
			rolesToDisplay={departmentNurseTypes}
			// The actions are suppressed on past days entirely, so the panel only ever opens on the plan.
			showActual={false}
			lockedTimes={prefill && { startTime: prefill.startTime, endTime: prefill.endTime }}
			defaultJobId={prefill?.jobId}
			// A window-level action posts THAT one prefilled shift, so its manual row is auto-checked and the
			// recommendation list is out of the way. A day-level action has no such intent — it keeps them.
			hideRecommendations={prefill !== undefined}
			// A day-level action (no prefilled window) has no shift yet — Add Staff opens on "Pick a shift first".
			initialShiftSelected={prefill !== undefined}
		/>
	);
};

const TAB_TO_EVENT: Record<MonthlyDayActionTab, string> = {
	"add-nurse": "monthly_view_add_nurse_clicked",
	"remove-nurse": "monthly_view_remove_staff_clicked",
	"create-shift": "monthly_view_post_shift_clicked",
};

export type UseMonthlyDayActionsParams = {
	/**
	 * `undefined` means this column spills past the loaded `hospitalData.weeks` (the partial trailing row
	 * of the visible window). There is then no `weeks[sourceWeekIndex]` for the panel to date the cell
	 * from, so the actions have to be refused rather than opened onto a crash.
	 */
	day: MonthlyDayData | undefined;
	date: DateTime;
	/**
	 * SOURCE (Sunday-indexed) coordinates — never the visible column. The panel indexes
	 * `hospitalData.weeks[weekIndex].weeklyShifts[…].days[dayIndex]` with them, and with a non-Sunday
	 * department week start a row's trailing columns come from `sourceWeekIndex + 1`, so the ambient
	 * `useWeekIndex()` would point at the previous data-week and change the wrong day.
	 */
	sourceWeekIndex: number;
	sourceDayIndex: number;
	/** Past days render actuals; there is no plan to add to, remove from, or post against. */
	showActual: boolean;
	/**
	 * Passed IN rather than read from `useHospitalData()` here — on purpose. This hook runs in all 42 grid
	 * cells, and subscribing every one of them to the hospital-data context for a single department-level
	 * boolean would re-render the whole grid on any change to it (see `MonthlyJobCell`'s memo note). The
	 * view reads it once and threads it down.
	 */
	isAutoScheduler: boolean;
	source: MonthlyDayActionSource;
};

export type MonthlyDayActionsApi = {
	/** Whether the actions can run right now. */
	canAct: boolean;
	/** Already-translated reason they can't; `null` while they are live. */
	disabledReason: string | null;
	/**
	 * Whether this DEPARTMENT posts open shifts at all — a configuration fact, not a per-day one.
	 *
	 * `StaffingChanges` omits the Post Shift tab entirely for an auto-scheduler department, which plans
	 * its own shifts. The monthly view was opening that panel with `defaultTab="create-shift"` regardless,
	 * and because the tab is merely absent from the strip rather than refused by the panel, the content
	 * mounted anyway — a flow reached through a door nobody meant to leave open. Surfaces use this to
	 * decide whether to RENDER a post affordance; a dead control for something the department can never do
	 * is noise.
	 */
	offersPost: boolean;
	/**
	 * Whether posting can run right now: `offersPost` AND `canAct`. Every surface that offers a post (the
	 * cell's icon row, its `P` shortcut, the drawer header, the drawer's per-window chip) asks this ONE
	 * question rather than each re-deriving it.
	 */
	canPost: boolean;
	/**
	 * Opens the ONE staffing-changes panel on `tab`. `prefill` is for an action raised from a single
	 * shift-time window — omit it from the day's generic action zone rather than inventing times.
	 * Refuses a tab the department doesn't offer rather than opening it on a hidden one.
	 */
	openPanel: (tab: MonthlyDayActionTab, prefill?: MonthlyDayActionPrefill) => void;
	/**
	 * Render slot for the panel. Renders NOTHING until an action is actually invoked — 42 cells must not
	 * mount 42 dormant panels — so it has to be placed in the tree even though it is usually empty.
	 */
	overlays: ReactNode;
};

/**
 * The ONE action layer behind the monthly view's day actions, shared by the day cell, the day sidebar
 * header and the sidebar's per-window rows. Wiring them separately is how three surfaces end up with
 * three sets of guards.
 *
 * All three actions open the app's single established surface for them — `StaffingChanges`, the tabbed
 * `MultiSizePopup` the weekly grid, the day view and the recommendations panel already use — on the tab
 * the user asked for: **Add Nurse**, **Remove Nurse** or **Post Shift**. Nothing here reimplements a
 * flow: the panel owns the agency/OT/PTO warnings, the reason-gated removal, the overlap check and the
 * recommendations, and it throws in swap/move/recurring for free. The monthly view's only job is to say
 * WHICH day, WHICH tab, and WHAT to prefill.
 *
 * Draft mode disables everything. `DraftModeProvider.calculateDraftChanges` is scoped to one
 * `activeDayData` day plus the active `weekIndex`, so a change made on any other day would be dropped
 * silently at publish time. Draft mode is a Day-Schedule feature and is not normally reachable from
 * here; this is a correctness backstop, not a common path.
 *
 * NOTE (deferred, own ticket): there is no read-only gate. `userInfo.is_read_only` and
 * `Department.is_readonly` are declared but read NOWHERE in `frontend/src`, and no other screen gates on
 * them — introducing one here alone would make the monthly view the only read-only-aware surface in the
 * app. The gate that exists today is the route's `access: HEAD_NURSE`.
 */
export const useMonthlyDayActions = ({
	day,
	date,
	sourceWeekIndex,
	sourceDayIndex,
	showActual,
	isAutoScheduler,
	source,
}: UseMonthlyDayActionsParams): MonthlyDayActionsApi => {
	const { t } = useTranslation();
	const { track } = useAnalytics();
	const { isDraftMode } = useDraftMode();
	const [panel, setPanel] = useState<{ tab: MonthlyDayActionTab; prefill?: MonthlyDayActionPrefill } | null>(null);

	const isDayLoaded = day !== undefined;
	const canAct = !showActual && !isDraftMode && isDayLoaded;
	// Mirrors `StaffingChanges`' own `!isAutoScheduler` gate on the Post Shift tab, so the door and the
	// room behind it agree about whether it exists.
	const offersPost = !isAutoScheduler;
	const canPost = canAct && offersPost;
	// Draft mode first: it is the reason a scheduler can act on, and it outranks an unloaded cell.
	const disabledReason = isDraftMode
		? t("monthly.view.actions.draft.mode.blocked")
		: !isDayLoaded && !showActual
			? t("monthly.view.actions.day.not.loaded")
			: null;
	const dateKey = date.toFormat("yyyy-MM-dd");

	const closePanel = useCallback(() => setPanel(null), []);

	const openPanel = useCallback(
		(tab: MonthlyDayActionTab, prefill?: MonthlyDayActionPrefill) => {
			if (!canAct) return;
			if (tab === "create-shift" && !canPost) return;
			setPanel({ tab, prefill });
			track(TAB_TO_EVENT[tab], { date: dateKey, source, prefilled: prefill !== undefined });
		},
		[canAct, canPost, track, dateKey, source],
	);

	/**
	 * Note for hosts: the panel portals to `document.body`, but React still bubbles its events through the
	 * React TREE — so a host that renders this inside a clickable surface must reject events that did not
	 * originate in its own DOM subtree (see `MonthlyJobCell`'s click guard, and `activateOnKeyMap`'s
	 * `target === currentTarget` check).
	 *
	 * Keyed on the tab so a second action raised while the panel is somehow still mounted remounts it:
	 * `StaffingChanges` seeds its active tab from `defaultTab` in `useState`, so a live instance would
	 * otherwise keep showing the tab the user asked for first.
	 */
	const overlays = panel && (
		<MonthlyDayStaffingChanges
			key={panel.tab}
			tab={panel.tab}
			prefill={panel.prefill}
			weekIndex={sourceWeekIndex}
			dayNo={sourceDayIndex}
			onClose={closePanel}
		/>
	);

	return { canAct, disabledReason, offersPost, canPost, openPanel, overlays };
};
